<template>
  <div class="store-users-view">
    <div>
      <div
        v-if="!currentStore"
        class="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
      >
        Select a store to manage its users.
      </div>

      <div
        v-else-if="!canAccessUsers"
        class="rounded-lg border border-amber-200 bg-amber-50 p-8 text-center text-sm text-amber-800 shadow-sm dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
      >
        You do not have permission to manage users for this store.
      </div>

      <store-users-management-modal
        v-else
        embedded
        :store="currentStore"
        :can-read-users="canReadUsers"
        :can-write-users="canWriteUsers"
        :can-manage-users="canManageUsers"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import StoreUsersManagementModal from "@/components/admin/store-users/StoreUsersManagementModal.vue";

export default {
  name: "StoreUsersView",
  components: {
    StoreUsersManagementModal,
  },
  computed: {
    ...mapGetters(["getUserStores", "getStoreById", "hasAdminPermissions"]),
    rawSelectedStore() {
      return this.$store.state.dashboard?.selectedStore ?? null;
    },
    selectedStoreId() {
      const value = this.rawSelectedStore || this.$route.params?.storeId;
      if (!value) {
        return null;
      }

      if (typeof value === "object") {
        return value?.id ?? null;
      }

      return value;
    },
    currentStore() {
      if (this.rawSelectedStore && typeof this.rawSelectedStore === "object") {
        return this.rawSelectedStore;
      }

      if (!this.selectedStoreId) {
        return null;
      }

      const resolver = this.getStoreById;
      if (typeof resolver === "function") {
        const store = resolver(this.selectedStoreId);
        if (store) {
          return store;
        }
      }

      return (this.getUserStores || []).find(
        (store) => String(store.id) === String(this.selectedStoreId),
      );
    },
    permissionCodes() {
      const permissions = Array.isArray(this.currentStore?.permissions)
        ? this.currentStore.permissions
        : [];

      return Array.from(new Set(permissions))
        .filter(Boolean)
        .map((permission) => String(permission).toUpperCase());
    },
    isStoreOwner() {
      return Boolean(this.currentStore?.isOwner);
    },
    canReadUsers() {
      return (
        this.hasAdminPermissions ||
        this.isStoreOwner ||
        this.hasStorePermission("CAN_READ_USERS") ||
        this.hasStorePermission("CAN_WRITE_USERS") ||
        this.hasStorePermission("CAN_MANAGE_USERS")
      );
    },
    canWriteUsers() {
      return (
        this.hasAdminPermissions ||
        this.isStoreOwner ||
        this.hasStorePermission("CAN_WRITE_USERS") ||
        this.hasStorePermission("CAN_MANAGE_USERS")
      );
    },
    canManageUsers() {
      return (
        this.hasAdminPermissions ||
        this.isStoreOwner ||
        this.hasStorePermission("CAN_MANAGE_USERS")
      );
    },
    canAccessUsers() {
      return this.canReadUsers || this.canWriteUsers || this.canManageUsers;
    },
  },
  methods: {
    hasStorePermission(permission) {
      return this.permissionCodes.includes(String(permission).toUpperCase());
    },
  },
};
</script>
